import React, { useEffect, useState } from "react";
import { X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { merchantCardsApi, errMsg } from "@/lib/merchantCards";

export function ApplyCardModal({ onClose, onSuccess }) {
  const [users, setUsers] = useState([]);
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({
    user_id: "", card_type_id: "", dial_code: "1", phone_number: "", email: "",
    initial_load: 0, address: "", city: "", country_area: "US", post_code: "",
    first_name: "", last_name: "", province: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([merchantCardsApi.users(), merchantCardsApi.cardTypes()]).then(([u, t]) => {
      setUsers(u.data.items || []);
      setTypes(t.data.items || []);
    });
  }, []);

  const selectedType = types.find((t) => t.id === form.card_type_id);
  const isPhysical = (selectedType?.form_factor || "").toLowerCase() === "physical";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        user_id: form.user_id,
        card_type_id: form.card_type_id,
        dial_code: form.dial_code,
        phone_number: form.phone_number,
        email: form.email || undefined,
        initial_load: Number(form.initial_load) || 0,
      };
      if (isPhysical) {
        body.postal_address = {
          firstName: form.first_name, lastName: form.last_name,
          address: form.address, province: form.province, city: form.city,
          countryArea: form.country_area, postCode: form.post_code,
        };
      } else {
        body.billing_address = {
          address: form.address, city: form.city,
          countryArea: form.country_area, postCode: form.post_code,
        };
      }
      await merchantCardsApi.apply(body);
      toast.success("Card application submitted");
      onSuccess();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Apply for card" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3 text-sm">
        <Field label="Cardholder">
          <select className="m-input" required value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}>
            <option value="">Select user…</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email}) — KYC: {u.kyc_status}</option>
            ))}
          </select>
        </Field>
        <Field label="Card type">
          <select className="m-input" required value={form.card_type_id} onChange={(e) => setForm({ ...form, card_type_id: e.target.value })}>
            <option value="">Select type…</option>
            {types.map((t) => (
              <option key={t.id} value={t.id}>{t.name} ({t.form_factor})</option>
            ))}
          </select>
        </Field>
        
          <div className="grid grid-cols-2 gap-3">
            <Field label="Dial code"><input className="m-input" required value={form.dial_code} onChange={(e) => setForm({ ...form, dial_code: e.target.value })} /></Field>
            <Field label="Phone"><input className="m-input" required value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} /></Field>
          </div>
        
        <Field label="Email (optional)"><input className="m-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
        <Field label="Initial load (USD)"><input className="m-input" type="number" min="0" step="0.01" value={form.initial_load} onChange={(e) => setForm({ ...form, initial_load: e.target.value })} /></Field>
        {isPhysical && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name"><input className="m-input" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></Field>
              <Field label="Last name"><input className="m-input" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></Field>
            </div>
            <Field label="Province"><input className="m-input" required value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} /></Field>
          </>
        )}
        <Field label="Address"><input className="m-input" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="City"><input className="m-input" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
          <Field label="Country"><input className="m-input" required maxLength={2} value={form.country_area} onChange={(e) => setForm({ ...form, country_area: e.target.value.toUpperCase() })} /></Field>
          <Field label="Post code"><input className="m-input" required value={form.post_code} onChange={(e) => setForm({ ...form, post_code: e.target.value })} /></Field>
        </div>
        
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="m-btn-outline">Cancel</button>
            <button type="submit" disabled={busy} className="m-btn-dark">{busy ? "Submitting…" : "Apply"}</button>
          </div>
        
      </form>
    </Modal>
  );
}

export function BindCardModal({ onClose, onSuccess }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    user_id: "", active_code: "", card_number: "", address: "", city: "",
    country_area: "US", post_code: "", dial_code: "1", phone_number: "", email: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    merchantCardsApi.users().then((r) => setUsers(r.data.items || []));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await merchantCardsApi.bind({ ...form, dial_code: Number(form.dial_code) });
      toast.success("Physical card bound");
      onSuccess();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Bind physical card" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3 text-sm">
        <Field label="Cardholder">
          <select className="m-input" required value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}>
            <option value="">Select user…</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Activation code"><input className="m-input" required value={form.active_code} onChange={(e) => setForm({ ...form, active_code: e.target.value })} /></Field>
          <Field label="Card number"><input className="m-input" required value={form.card_number} onChange={(e) => setForm({ ...form, card_number: e.target.value })} /></Field>
        </div>
        <Field label="Email"><input className="m-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Dial code"><input className="m-input" required value={form.dial_code} onChange={(e) => setForm({ ...form, dial_code: e.target.value })} /></Field>
          <Field label="Phone"><input className="m-input" required value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} /></Field>
        </div>
        <Field label="Address"><input className="m-input" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="City"><input className="m-input" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
          <Field label="Country"><input className="m-input" required maxLength={2} value={form.country_area} onChange={(e) => setForm({ ...form, country_area: e.target.value.toUpperCase() })} /></Field>
          <Field label="Post code"><input className="m-input" required value={form.post_code} onChange={(e) => setForm({ ...form, post_code: e.target.value })} /></Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="m-btn-outline">Cancel</button>
          <button type="submit" disabled={busy} className="m-btn-dark">{busy ? "Binding…" : "Bind card"}</button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white border border-zinc-200 rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
          <h2 className="font-display text-xl tracking-tight font-medium">{title}</h2>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-950"><X size={18} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">{label}</span>
      {children}
    </label>
  );
}
